/**
 * dtStatus — THE single source for DT Alpha status/priority/origin domains
 * (Categories.ts pattern): service-call (ticket) lifecycle + priority + origin,
 * and IoT device status. Every page/chip/badge derives from these maps —
 * component files must not re-declare them (DashboardPage/DiagramsPage copies
 * drifted within a week of being written; DiagramsPage's dropped 'disabled').
 */

// ---------------------------------------------------------------------------
// Service calls (tickets) — Epic B
// ---------------------------------------------------------------------------

export type ServiceCallPriority = 'low' | 'normal' | 'high' | 'critical';
export type ServiceCallOrigin = 'manual' | '3d_element' | 'diagram' | 'iot';
export type CallStatus = 'open' | 'assigned' | 'in_progress' | 'resolved' | 'closed' | 'cancelled';

/**
 * The DT design-language semantic colours (theme.css :root --dt-*), as the literal hex values.
 * Every status/priority colour below is one of these — not a near miss (the maps drifted to
 * #e0a63a / #e05a5a / #9d78f2 against the tokens' #dd9a2b / #e0574f / #8f78ef). Hex, not
 * var(), because some consumers are not CSS (diagram node badges); the semantic tokens have
 * one value in both themes, so the hex is exact. `neutral`/`muted` are --dt-text-3 / -2 as
 * they read in the light theme. ticketsDesignLanguage.test.ts pins each value against theme.css.
 */
export const DT_TOKEN_COLORS = {
  good: '#2f9e6e',    // --dt-good
  warn: '#dd9a2b',    // --dt-warn
  bad: '#e0574f',     // --dt-bad
  prog: '#8f78ef',    // --dt-prog
  info: '#48CAE4',    // --dt-info
  neutral: '#8695a4', // --dt-text-3
  muted: '#586a7b',   // --dt-text-2
} as const;

export const PRIORITY_COLORS: Record<ServiceCallPriority, string> = {
  low: DT_TOKEN_COLORS.neutral,
  normal: DT_TOKEN_COLORS.info,
  high: DT_TOKEN_COLORS.warn,
  critical: DT_TOKEN_COLORS.bad,
};

export const ORIGIN_LABELS: Record<ServiceCallOrigin, string> = {
  manual: 'Manual',
  '3d_element': '3D element',
  diagram: 'Diagram',
  iot: 'IoT',
};

export const CALL_STATUS_LABELS: Record<CallStatus, string> = {
  open: 'Open',
  assigned: 'Assigned',
  in_progress: 'In progress',
  resolved: 'Resolved',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

export const CALL_STATUS_COLORS: Record<CallStatus, string> = {
  open: DT_TOKEN_COLORS.warn,
  assigned: DT_TOKEN_COLORS.info,
  in_progress: DT_TOKEN_COLORS.prog,
  resolved: DT_TOKEN_COLORS.good,
  closed: DT_TOKEN_COLORS.neutral,
  cancelled: DT_TOKEN_COLORS.neutral,
};

/** Statuses that count as "being worked" (dashboard stats, default list filter). */
export const ACTIVE_CALL_STATUSES: ReadonlySet<CallStatus> = new Set(['open', 'assigned', 'in_progress']);

// ---------------------------------------------------------------------------
// IoT devices — Epic D (status is DERIVED server-side, see iotDevices.js)
// ---------------------------------------------------------------------------

export type DeviceStatus = 'ok' | 'alert' | 'offline' | 'never-reported' | 'disabled';

export const DEVICE_STATUS_COLORS: Record<DeviceStatus, string> = {
  ok: DT_TOKEN_COLORS.good,
  alert: DT_TOKEN_COLORS.bad,
  offline: DT_TOKEN_COLORS.neutral,
  'never-reported': DT_TOKEN_COLORS.warn,
  disabled: DT_TOKEN_COLORS.muted,
};

export const DEVICE_STATUS_LABELS: Record<DeviceStatus, string> = {
  ok: 'OK',
  alert: 'Alert',
  offline: 'Offline',
  'never-reported': 'No data yet',
  disabled: 'Disabled',
};

// ---------------------------------------------------------------------------
// i18n: value → TranslationContext key. Render with t(MAP[value]) so the same
// status/priority/origin reads in the active language everywhere. The English
// *_LABELS maps above stay as the source-of-truth fallback / non-UI use.
// ---------------------------------------------------------------------------

export const CALL_STATUS_LABEL_KEYS: Record<CallStatus, string> = {
  open: 'dt_status_open',
  assigned: 'dt_status_assigned',
  in_progress: 'dt_status_in_progress',
  resolved: 'dt_status_resolved',
  closed: 'dt_status_closed',
  cancelled: 'dt_status_cancelled',
};

export const PRIORITY_LABEL_KEYS: Record<ServiceCallPriority, string> = {
  low: 'dt_priority_low',
  normal: 'dt_priority_normal',
  high: 'dt_priority_high',
  critical: 'dt_priority_critical',
};

export const ORIGIN_LABEL_KEYS: Record<ServiceCallOrigin, string> = {
  manual: 'dt_origin_manual',
  '3d_element': 'dt_origin_3d_element',
  diagram: 'dt_origin_diagram',
  iot: 'dt_origin_iot',
};

export const DEVICE_STATUS_LABEL_KEYS: Record<DeviceStatus, string> = {
  ok: 'dt_devstatus_ok',
  alert: 'dt_devstatus_alert',
  offline: 'dt_devstatus_offline',
  'never-reported': 'dt_devstatus_never',
  disabled: 'dt_devstatus_disabled',
};

/** Classification is independent of where a ticket originated. */
export type TicketIssueType = 'incident' | 'maintenance' | 'inspection' | 'request' | 'other';
export const ISSUE_TYPE_LABEL_KEYS: Record<TicketIssueType, string> = {
  incident: 'dt_issue_incident', maintenance: 'dt_issue_maintenance', inspection: 'dt_issue_inspection',
  request: 'dt_issue_request', other: 'dt_issue_other',
};
